import { Icon, type LucideIcon, type LucideProps, type SVGAttributes, useLucideContext } from 'lucide-react';
import * as React from 'react';

import { hasA11yProp } from './hasA11yProp';

/** A Lucide icon node without children. */
type FlatIconNode = [tag: string, attributes: SVGAttributes];

/** Lucide has no filled icons: this fills the outer shape and cuts the glyph out (icons.md, "Filled or outline"). */
function createFilledIcon(
    displayName: string,
    [shape, ...glyph]: [FlatIconNode, ...FlatIconNode[]],
    // Drawn as strokes, like a lock's shackle: not filled, but masked like the shape, so a glyph cuts them too.
    { stroked = [] }: { stroked?: FlatIconNode[] } = {},
): LucideIcon {
    // As on a Lucide icon, `absoluteStrokeWidth` scales by the `size` prop, not a slot's size: wrong in a slot.
    const FilledIcon = React.forwardRef<SVGSVGElement, LucideProps>(
        ({ color, nonScalingStroke, className, children, ...props }, ref) => {
            const maskId = React.useId();
            const context = useLucideContext();
            const paint = color ?? context.color ?? 'currentColor';
            const strokeEffect =
                (nonScalingStroke ?? context.nonScalingStroke) === true ? { vectorEffect: 'non-scaling-stroke' } : {};
            // One rect paints through the mask, so a translucent color doesn't paint the stroke's inner half twice.
            return (
                <Icon
                    ref={ref}
                    iconNode={[]}
                    color={paint}
                    className={className === undefined ? 'lucide-filled' : `lucide-filled ${className}`}
                    // Lucide hides an icon from screen readers only when it has no children, and the mask is one.
                    aria-hidden={Boolean(children) || hasA11yProp(props) ? undefined : 'true'}
                    {...props}
                >
                    <mask id={maskId} maskUnits="userSpaceOnUse" x={0} y={0} width={24} height={24}>
                        {React.createElement(shape[0], {
                            ...strokeEffect,
                            ...shape[1],
                            fill: 'white',
                            stroke: 'white',
                        })}
                        {stroked.map(([tag, attributes], index) =>
                            React.createElement(tag, {
                                ...strokeEffect,
                                ...attributes,
                                key: `stroked-${index}`,
                                fill: 'none',
                                stroke: 'white',
                            }),
                        )}
                        {glyph.map(([tag, attributes], index) =>
                            React.createElement(tag, {
                                ...strokeEffect,
                                ...attributes,
                                key: index,
                                fill: 'none',
                                stroke: 'black',
                            }),
                        )}
                    </mask>
                    <rect width={24} height={24} fill={paint} stroke="none" mask={`url(#${maskId})`} />
                    {children}
                </Icon>
            );
        },
    );
    FilledIcon.displayName = displayName;
    return FilledIcon;
}

// The shapes of lucide-react 1.46.0's icons, which it doesn't export. The test checks that they still match.
const CIRCLE: FlatIconNode = ['circle', { cx: '12', cy: '12', r: '10' }];

export const CircleAlertFilledIcon = createFilledIcon('CircleAlertFilled', [
    CIRCLE,
    ['line', { x1: '12', x2: '12', y1: '8', y2: '12' }],
    ['line', { x1: '12', x2: '12.01', y1: '16', y2: '16' }],
]);

export const CircleCheckFilledIcon = createFilledIcon('CircleCheckFilled', [
    CIRCLE,
    ['path', { d: 'm16 9-5.5 5.5L8 12' }],
]);

export const CircleQuestionMarkFilledIcon = createFilledIcon('CircleQuestionMarkFilled', [
    CIRCLE,
    ['path', { d: 'M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3' }],
    ['path', { d: 'M12 17h.01' }],
]);

export const CircleXFilledIcon = createFilledIcon('CircleXFilled', [
    CIRCLE,
    ['path', { d: 'm15 9-6 6' }],
    ['path', { d: 'm9 9 6 6' }],
]);

export const InfoFilledIcon = createFilledIcon('InfoFilled', [
    CIRCLE,
    ['path', { d: 'M12 16v-4' }],
    ['path', { d: 'M12 8h.01' }],
]);

export const TriangleAlertFilledIcon = createFilledIcon('TriangleAlertFilled', [
    ['path', { d: 'm21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3' }],
    ['path', { d: 'M12 9v4' }],
    ['path', { d: 'M12 17h.01' }],
]);

// The same body as LockOpen's, so a lock toggle doesn't jump.
const LOCK_BODY: FlatIconNode = ['rect', { width: '18', height: '11', x: '3', y: '11', rx: '2', ry: '2' }];
const LOCK_SHACKLE: FlatIconNode = ['path', { d: 'M7 11V7a5 5 0 0 1 10 0v4' }];

/** Fully locked; the outline LockIcon is partly locked, and LockOpenIcon unlocked. */
export const LockFilledIcon = createFilledIcon('LockFilled', [LOCK_BODY, ['circle', { cx: '12', cy: '16', r: '1' }]], {
    stroked: [LOCK_SHACKLE],
});
